import * as React from 'react';

import { once } from 'lodash';
import { Query } from 'react-apollo';

import AdminSisSyncConnectedView from 'admin/rostering/components/AdminSisSyncConnectedView';
import AdminSisSyncHoldView from 'admin/rostering/components/AdminSisSyncHoldView';
import AdminSisSyncInReviewView from 'admin/rostering/components/AdminSisSyncInReviewView';
import AdminSisSyncPausedView from 'admin/rostering/components/AdminSisSyncPausedView';
import AdminSisSyncProcessingView from 'admin/rostering/components/AdminSisSyncProcessingView';
import AdminSisSyncSchoolNotSetupView from 'admin/rostering/components/AdminSisSyncSchoolNotSetupView';
import ErrorReporter from 'app/components/ErrorReporter';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import LinkConstants from 'constants/LinkConstants';
import EventTracking from 'lib/EventTracking';
import { findMatchingRecord } from 'utils/arrayHelpers';

import {
  AdminSchoolSisDashboard as AdminSchoolSisDashboard_Data,
  AdminSchoolSisDashboard_organizations as Organization,
  AdminSchoolSisDashboardVariables,
} from 'admin/rostering/queries/__generated__/AdminSchoolSisDashboard';

import ADMIN_SCHOOL_SIS_DASHBOARD_QUERY from 'admin/rostering/queries/adminSchoolSisDashboardQuery.gql';

type Props = {
  orgUuid: string;
  loading: boolean;
};

class AdminSchoolSisDashboard extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.view', {
      organization_uuid: this.props.orgUuid,
    });
  });

  renderSyncStatus(organization: Organization) {
    const { sisSyncOverview } = organization;
    const {
      canOnlySeeReviewScreen,
      canOnlySeeRosteringStagingArea,
    } = organization.rosteringPermissions;

    if (!sisSyncOverview) {
      return (
        <ErrorReporter error={'AdminSchoolSisDashboard - sisSyncOverview is null'}>
          <span>Uh, oh. You should not be here, something weird happened.</span>
        </ErrorReporter>
      );
    }
    switch (sisSyncOverview.syncStatus ? sisSyncOverview.syncStatus.toLowerCase() : '') {
      case 'not_set_up':
        return <AdminSisSyncSchoolNotSetupView />;
      case 'on_hold':
        const { holdSummary, syncMethod } = sisSyncOverview;

        if (holdSummary && syncMethod) {
          return (
            <AdminSisSyncHoldView
              orgUuid={organization.uuid}
              holdSummary={holdSummary}
              syncMethod={syncMethod}
            />
          );
        } else {
          return null;
        }
      case 'connected':
        return (
          <AdminSisSyncConnectedView
            orgUuid={organization.uuid}
            orgId={organization.id}
            sisSyncOverview={sisSyncOverview}
            className="AdminSchoolSisDashboard"
            isStagingArea={canOnlySeeRosteringStagingArea}
          />
        );
      case 'processing':
        return (
          <AdminSisSyncProcessingView
            orgUuid={organization.uuid}
            status="processing"
            subtext={<span>We’re still processing your data. We’ll be done soon!</span>}
          />
        );
      case 'failed':
        return (
          <AdminSisSyncProcessingView
            orgUuid={organization.uuid}
            status="failed"
            subtext={
              <span>
                Your district's sync is currently in progress. Please reach out to a district
                administrator for updates.
              </span>
            }
          />
        );
      case 'paused':
        return (
          <AdminSisSyncPausedView
            orgUuid={organization.uuid}
            subtext={
              <span>
                Your sync is currently paused. If you believe this is incorrect,
                <Link href={LinkConstants.CONTACT_FORM} target="_blank">
                  {' '}
                  contact support
                </Link>
                .
              </span>
            }
          />
        );
      case 'in_review':
        if (canOnlySeeReviewScreen) {
          return <AdminSisSyncInReviewView orgUuid={organization.uuid} />;
        }
        return (
          <AdminSisSyncConnectedView
            orgUuid={organization.uuid}
            orgId={organization.id}
            sisSyncOverview={sisSyncOverview}
            className="AdminSchoolSisDashboard"
            isStagingArea={canOnlySeeRosteringStagingArea}
          />
        );
      default:
        console.error('AdminSchoolSisDashboard - syncStatus not recognized');
        return <span>Uh, oh. You should not be here, something weird happened.</span>;
    }
  }

  render() {
    const { loading, orgUuid } = this.props;

    if (loading) {
      return null;
    }

    return (
      <Query<AdminSchoolSisDashboard_Data, AdminSchoolSisDashboardVariables>
        query={ADMIN_SCHOOL_SIS_DASHBOARD_QUERY}
        variables={{
          organizationUuid: [orgUuid],
        }}>
        {({ data, error, loading }) => {
          if (error) {
            return <ErrorReporter error={error} />;
          }

          if (loading || !data?.organizations) {
            return <LoadingIndicator height={400} size="small" />;
          }

          const org = findMatchingRecord<Organization>(data?.organizations, orgUuid);

          if (!org) {
            console.error('AdminSchoolSisDashboard expected an organization but found none');
            return null;
          }
          return this.renderSyncStatus(org);
        }}
      </Query>
    );
  }
}

export default AdminSchoolSisDashboard;
